import { motion } from "framer-motion";
import { Clipboard, Globe, ChevronDown } from "lucide-react";
import { useI18n, type Locale } from "@/lib/i18n";
import { useState, useRef, useEffect } from "react";

const languages: { code: Locale; label: string }[] = [
  { code: 'ko', label: 'lang.ko' },
  { code: 'en', label: 'lang.en' },
  { code: 'ja', label: 'lang.ja' },
];

interface BrandBarProps {
  onIconClick?: () => void;
}

const BrandBar = ({ onIconClick }: BrandBarProps) => {
  const { locale, setLocale, t } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <motion.header
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="glass-bar rounded-xl px-5 py-2.5 flex items-center justify-between relative z-[200]"
    >
      <div className="flex items-center gap-4">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onIconClick}
          className="rounded-xl p-0 shrink-0"
          title={t('notice.title')}
        >
          <img
            src="/icon.png"
            alt="Brand Icon"
            className="w-16 h-16 object-contain rounded-xl border border-primary/60 shadow-sm"
          /* drop-shadow-[0_4px_6px_rgba(0,0,0,0.4)] */
          />
        </motion.button>
        <h1 className="text-2xl font-bold tracking-tight leading-tight bg-gradient-to-br from-primary via-primary/80 to-primary/60 bg-clip-text text-transparent whitespace-pre-wrap">
          {t('app.name')}
        </h1>
      </div>

      <div className="relative" ref={ref}>
        <button
          onClick={() => setOpen(!open)}
          data-testid="language-toggle"
          className="glass-panel rounded-lg px-3 py-1.5 text-sm text-muted-foreground flex items-center gap-1.5"
        >
          <Globe className="w-4 h-4" />
          <span>{t(`lang.${locale}`)}</span>
          <ChevronDown className="w-3 h-3" />
        </button>

        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            className="absolute right-0 top-full mt-1 z-[9999] glass-panel rounded-lg overflow-hidden min-w-[120px]"
          >
            {languages.map(({ code, label }) => (
              <button
                key={code}
                data-testid={`lang-option-${code}`}
                onClick={() => { setLocale(code); setOpen(false); }}
                className={`w-full px-3 py-2 text-sm text-left hover:bg-primary/10 transition-colors ${locale === code ? 'text-primary font-medium' : 'text-muted-foreground'
                  }`}
              >
                {t(label)}
              </button>
            ))}
          </motion.div>
        )}
      </div>
    </motion.header>
  );
};

export default BrandBar;
